import * as Select from "@radix-ui/react-select";
import clsx from "clsx";
import React, { forwardRef } from "react";
import Chevron from "../../../../app/assets/images/svg/chevron.svg";
import styles from "./SelectInput.module.scss";

interface Props {
	label: string;
	placeHolder: string;
	options?: {
		text?: string;
		value?: string | number;
		label?: string;
		isLabel?: boolean;
		disabled?: boolean;
	}[];
	errorMessage?: string;
	disabled?: boolean;
	defaultValue?: string;
	value?: string;
	onChange?: any;
	prefix?: string;
	className?: string;
}

const SelectInput = forwardRef<HTMLButtonElement, Props>((props, ref) => {
	const {
		label,
		placeHolder,
		options,
		errorMessage = false,
		disabled = false,
		prefix,
		value,
		defaultValue,
		onChange,
		className = "",
	} = props;

	const optionsHtml = () => {
		if (!options) return <SelectItem value=''>No options provided</SelectItem>;
		return options?.map(({ text, label, value, isLabel, disabled }, index) => {
			// Return an optional label if requested
			if (isLabel)
				return (
					<Select.Label key={text ?? index} className={styles.label}>
						{text || label}
					</Select.Label>
				);

			return (
				<SelectItem
					disabled={disabled}
					key={text ?? index}
					value={value?.toString() || text || label || ""}
				>
					{text || label}
				</SelectItem>
			);
		});
	};

	return (
		<div
			className={clsx([
				styles.container,
				disabled ? styles.disabled : null,
				className,
			])}
		>
			<Select.Root
				onValueChange={onChange}
				value={value}
				defaultValue={defaultValue}
			>
				<Select.Trigger
					className={styles.trigger}
					aria-label={label}
					ref={ref}
					data-state={"open"}
				>
					<span>
						{prefix && <span className={styles.prefix}>{prefix}</span>}
						{value ?? placeHolder}
					</span>
					<Select.Icon className={styles.arrow}>
						<Chevron aria-labelledby='directional chevron' />
					</Select.Icon>
				</Select.Trigger>
				<Select.Content
					position='popper'
					side='bottom'
					align='start'
					className={`${styles.content} ${className}`}
				>
					<Select.ScrollUpButton className={clsx([styles.scroll, styles.up])}>
						<Chevron aria-labelledby='directional chevron' />
					</Select.ScrollUpButton>
					<Select.Viewport className={styles.viewport}>
						<Select.Group>{optionsHtml()}</Select.Group>
					</Select.Viewport>
					<Select.ScrollDownButton
						className={clsx([styles.scroll, styles.down])}
					>
						<Chevron aria-labelledby='directional chevron' />
					</Select.ScrollDownButton>
				</Select.Content>
			</Select.Root>
		</div>
	);
});

export default SelectInput;

type ItemProps = Select.SelectItemProps & React.RefAttributes<HTMLDivElement>;

const SelectItem = React.forwardRef<HTMLDivElement, ItemProps>(
	({ children, value, ...props }: ItemProps, forwardedRef) => {
		return (
			<Select.Item
				className={styles.item}
				value={value}
				{...props}
				ref={forwardedRef}
			>
				<Select.ItemText className={styles.itemText}>
					{children}
				</Select.ItemText>
			</Select.Item>
		);
	},
);
